import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Typography, Space, Statistic, Grid, Alert } from 'antd';
import { MailOutlined, SafetyOutlined, EditOutlined } from '@ant-design/icons';
import { useSelfRegistration } from '../../hooks/useSelfRegistration';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import { SelfRegistrationSessionDTO } from '@/shared/api/model';

const { Title, Text } = Typography;
const { Countdown } = Statistic;
const { useBreakpoint } = Grid;

interface EmailVerificationStepProps {
    session: SelfRegistrationSessionDTO;
    onComplete: (session: SelfRegistrationSessionDTO) => void;
    onError: (message: string) => void;
}

const EmailVerificationStep: React.FC<EmailVerificationStepProps> = ({
    session,
    onComplete,
    onError
}) => {
    const [form] = Form.useForm();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    
    const [otpSent, setOtpSent] = useState(false);
    const [canResend, setCanResend] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [isEditingEmail, setIsEditingEmail] = useState(false);
    const [currentEmail, setCurrentEmail] = useState(session.email || '');
    const lastInitiatedKeyRef = useRef<string>('');

    // Use the refactored self registration hook
    const {
        initiateEmailVerification,
        verifyEmail,
        resendEmailOtp,
        isInitiatingEmailVerification,
        isVerifyingEmail,
        isResendingEmailOtp
    } = useSelfRegistration();

    useEffect(() => {
        // Auto-initiate on mount whenever we have a valid session+email (always send fresh OTP)
        if (!isEditingEmail && session.sessionToken && currentEmail) {
            const key = `${session.sessionToken}|${currentEmail}`;
            if (lastInitiatedKeyRef.current !== key) {
                lastInitiatedKeyRef.current = key;
                handleInitiateEmailVerification();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.sessionToken]);

    useEffect(() => {
        // Update current email if session email changes
        setCurrentEmail(session.email || '');
    }, [session.email]);

    const handleInitiateEmailVerification = async (emailToUse?: string) => {
        const emailAddress = emailToUse || currentEmail;
        console.log('🔍 handleInitiateEmailVerification called for email:', emailAddress);
        
        try {
            // Call the actual API using the refactored hook with both sessionToken and email
            console.log('🚀 Calling initiateEmailVerification API with:', {
                sessionToken: session.sessionToken,
                email: emailAddress
            });
            
            if (!session.sessionToken || !emailAddress) {
                throw new Error('Session token and email are required');
            }
            
            await initiateEmailVerification(session.sessionToken, emailAddress);
            
            console.log('✅ Email verification initiated successfully');
            setOtpSent(true);
            setIsEditingEmail(false);
            setCountdown(Date.now() + 30000);
            showSuccessNotification(`Email verification code sent to ${emailAddress}`);
            
        } catch (error: any) {
            console.error('❌ Error in handleInitiateEmailVerification:', error);
            // Extract the actual error message from the error object
            const errorMessage = error.message || 'Failed to send email verification';
            onError(errorMessage);
        }
    };

    const handleVerifyEmail = async (values: any) => {
        console.log('🔍 handleVerifyEmail called with values:', values);
        
        try {
            if (!session.sessionToken) {
                throw new Error('Session token is required');
            }

            const request = {
                sessionToken: session.sessionToken,
                otpCode: values.otpCode
            };

            console.log('🚀 Calling verifyEmail API with request:', request);
            
            // Call the actual API using the refactored hook and await the response
            const updatedSession = await verifyEmail(request);
            
            console.log('✅ Email verified successfully, updated session:', updatedSession);
            showSuccessNotification('Email verified successfully');
            
            // Call onComplete with the updated session to advance to next step
            onComplete(updatedSession);
            
        } catch (error: any) {
            console.error('❌ Error in handleVerifyEmail:', error);
            // Extract the actual error message from the error object
            const errorMessage = error.message || 'Invalid email verification code';
            onError(errorMessage);
        }
    };

    const handleResendOtp = async () => {
        if (!canResend) return;
        
        try {
            if (!session.sessionToken) {
                throw new Error('Session token is required');
            }
            
            // If email has been changed, use initiateEmailVerification instead of resend
            if (currentEmail !== session.email) {
                await handleInitiateEmailVerification(currentEmail);
                return;
            }
            
            // Call the actual API using the refactored hook
            await resendEmailOtp(session.sessionToken);
            setCountdown(Date.now() + 30000);
            setCanResend(false);
            showSuccessNotification('Email verification code resent');
            
        } catch (error: any) {
            // Extract the actual error message from the error object
            const errorMessage = error.message || 'Failed to resend email verification';
            onError(errorMessage);
        }
    };

    const handleEmailChange = (newEmail: string) => {
        setCurrentEmail(newEmail);
        if (newEmail !== session.email) {
            setOtpSent(false);
            setCanResend(false);
            setCountdown(0);
        }
    };

    const handleChangeEmail = () => {
        setIsEditingEmail(true);
        setOtpSent(false);
        setCanResend(false);
        setCountdown(0);
    };

    const handleConfirmEmailChange = async (values: any) => {
        const newEmail = values.email;
        if (!newEmail) {
            onError('Please enter a valid email address');
            return;
        }
        
        try {
            setCurrentEmail(newEmail);
            await handleInitiateEmailVerification(newEmail);
        } catch (error: any) {
            // Extract the actual error message from the error object
            const errorMessage = error.message || 'Failed to update email address';
            onError(errorMessage);
        }
    };

    const onCountdownFinish = () => {
        setCanResend(true);
        setCountdown(0);
    };

    if (!otpSent || isEditingEmail) {
        return (
            <div>
                <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '30px' }}>
                    <MailOutlined style={{ 
                        fontSize: isMobile ? '36px' : '48px', 
                        color: '#1890ff', 
                        marginBottom: isMobile ? '12px' : '16px' 
                    }} />
                    <Title level={isMobile ? 4 : 3}>
                        {isEditingEmail ? 'Update Email Address' : 'Email Verification'}
                    </Title>
                    <Text type="secondary" style={{ 
                        fontSize: isMobile ? '14px' : '16px',
                        display: 'block',
                        wordBreak: 'break-word'
                    }}>
                        {isEditingEmail 
                            ? 'Enter the correct email address to receive verification code'
                            : `Preparing to send verification code to ${currentEmail}`
                        }
                    </Text>
                </div>

                {isEditingEmail ? (
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleConfirmEmailChange}
                        size={isMobile ? 'middle' : 'large'}
                        initialValues={{ email: currentEmail }}
                    >
                        <Form.Item
                            name="email"
                            label="Email Address"
                            rules={[
                                { required: true, message: 'Please enter your email address' },
                                { type: 'email', message: 'Please enter a valid email address' }
                            ]}
                        >
                            <Input 
                                placeholder="your.email@example.com" 
                                style={{ fontSize: isMobile ? '16px' : '14px' }}
                                onChange={(e) => handleEmailChange(e.target.value)}
                            />
                        </Form.Item>

                        <Form.Item>
                            <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={isInitiatingEmailVerification}
                                    size={isMobile ? 'middle' : 'large'}
                                    style={{ 
                                        height: isMobile ? '44px' : '48px',
                                        fontSize: isMobile ? '16px' : '18px',
                                        width: isMobile ? '100%' : 'auto'
                                    }}
                                >
                                    Send Verification Code
                                </Button>
                                <Button
                                    onClick={() => setIsEditingEmail(false)}
                                    size={isMobile ? 'middle' : 'large'}
                                    style={{ 
                                        height: isMobile ? '44px' : '48px',
                                        fontSize: isMobile ? '16px' : '18px',
                                        width: isMobile ? '100%' : 'auto'
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                ) : (
                    <div style={{ textAlign: 'center' }}>
                        <Button
                            type="primary"
                            loading={isInitiatingEmailVerification}
                            onClick={() => handleInitiateEmailVerification()}
                            size={isMobile ? 'middle' : 'large'}
                            style={{ 
                                height: isMobile ? '44px' : '48px',
                                fontSize: isMobile ? '16px' : '18px'
                            }}
                        >
                            Send Email Verification
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '30px' }}>
                <SafetyOutlined style={{ 
                    fontSize: isMobile ? '36px' : '48px', 
                    color: '#52c41a', 
                    marginBottom: isMobile ? '12px' : '16px' 
                }} />
                <Title level={isMobile ? 4 : 3}>Verify Your Email</Title>
                <Text type="secondary" style={{ 
                    fontSize: isMobile ? '14px' : '16px',
                    display: 'block',
                    wordBreak: 'break-word'
                }}>
                    We've sent a verification code to {currentEmail}
                </Text>
                
                {/* Email change alert and button */}
                <div style={{ marginTop: isMobile ? '15px' : '20px' }}>
                    <Alert
                        message="Wrong email address?"
                        description="If you entered the wrong email address, you can change it below."
                        type="info"
                        showIcon
                        style={{ 
                            textAlign: 'left',
                            marginBottom: isMobile ? '15px' : '20px'
                        }}
                        action={
                            <Button 
                                size="small" 
                                type="link" 
                                icon={<EditOutlined />}
                                onClick={handleChangeEmail}
                                style={{ fontSize: isMobile ? '12px' : '14px' }}
                            >
                                Change Email
                            </Button>
                        }
                    />
                </div>
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleVerifyEmail}
                size={isMobile ? 'middle' : 'large'}
            >
                <Form.Item
                    name="otpCode"
                    label="Email Verification Code"
                    rules={[
                        { required: true, message: 'Please enter the verification code' },
                        { len: 6, message: 'Verification code must be 6 digits' }
                    ]}
                >
                    <Input
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                        style={{ 
                            textAlign: 'center', 
                            fontSize: isMobile ? '20px' : '18px', 
                            letterSpacing: isMobile ? '3px' : '4px',
                            height: isMobile ? '48px' : '40px'
                        }}
                    />
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={isVerifyingEmail}
                        block
                        size={isMobile ? 'middle' : 'large'}
                        style={{ 
                            height: isMobile ? '44px' : '48px',
                            fontSize: isMobile ? '16px' : '18px'
                        }}
                    >
                        Verify Email Address
                    </Button>
                </Form.Item>

                <div style={{ textAlign: 'center', marginTop: isMobile ? '15px' : '20px' }}>
                    {countdown > 0 ? (
                        <Space direction={isMobile ? 'vertical' : 'horizontal'} size="small">
                            <Text type="secondary" style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                Resend code in:
                            </Text>
                            <Countdown
                                value={countdown}
                                format="ss"
                                onFinish={onCountdownFinish}
                                valueStyle={{ fontSize: isMobile ? '16px' : '14px' }}
                            />
                        </Space>
                    ) : (
                        <Button
                            type="link"
                            onClick={handleResendOtp}
                            disabled={!canResend}
                            loading={isResendingEmailOtp}
                            size={isMobile ? 'small' : 'middle'}
                            style={{ fontSize: isMobile ? '14px' : '16px' }}
                        >
                            Resend Email Verification
                        </Button>
                    )}
                </div>

                <div style={{ 
                    marginTop: isMobile ? '20px' : '30px', 
                    padding: isMobile ? '12px' : '16px', 
                    backgroundColor: '#f6ffed', 
                    border: '1px solid #b7eb8f',
                    borderRadius: '6px'
                }}>
                    <Text type="secondary" style={{ 
                        fontSize: isMobile ? '11px' : '12px',
                        lineHeight: isMobile ? '1.4' : '1.2'
                    }}>
                        <strong>Tip:</strong> Check your spam/junk folder if you don't see the email.
                        The verification code is valid for 10 minutes.
                    </Text>
                </div>
            </Form>
        </div>
    );
};

export default EmailVerificationStep;
